import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";

import { SearchProductsQuery } from "../../../common/generated-types";
import { MOV_FILTER_QUERY_PARAM } from "../mov-filter/mov-filter.constants";
import { ShippingDateFilterComponent } from "../shipping-date-filter/shipping-date-filter.component";
import { MovFilterComponent } from "../mov-filter/mov-filter.component";
import { SupplyTypeFilterComponent } from "../supply-type-filter/supply-type-filter.component";
import {
    FACET_SEARCH_ROUTE_QUERY_PARAM,
    SHIPPING_DATE_FILTER_QUERY_PARAM,
} from "./product-list-controls.constants";
import { SUPPLY_TYPE_FILTER_QUERY_PARAM } from "../supply-type-filter/supply-type-filter.constants";
import { FacetWithValues } from "./product-list-controls.types";
import {
    buildProductListRouteCommands,
    getClearProductListFilterQueryParams,
} from "../product-list/product-list.utils";

@Component({
    selector: "vsf-product-list-controls",
    templateUrl: "./product-list-controls.component.html",
    styleUrls: ["./product-list-controls.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListControlsComponent implements OnChanges, OnInit, OnDestroy {
    @Input() activeFacetValueIds: string[] = [];
    @Input() facetValues: SearchProductsQuery["search"]["facetValues"] | null;
    @Input() totalResults = 0;
    @Input() facetTotalItems = 0;
    @Input() facetSearchTerm = "";
    @Input() loading = false;
    @Input() supplierId: string | null = null;
    @Input() showSupplyTypeFilter = false;
    @Input() isConsolidatedFeed = false;
    @Output() facetSearchTermChange = new EventEmitter<string>();
    @Output() openFilterModal = new EventEmitter<void>();
    @Output() requestMoreFacets = new EventEmitter<void>();
    @Output() collapseFilters = new EventEmitter<void>();
    @ViewChild("shippingFilter") shippingFilter!: ShippingDateFilterComponent;
    @ViewChild("movFilter") movFilter!: MovFilterComponent;
    @ViewChild("shippingFilterMobile")
    shippingFilterMobile!: ShippingDateFilterComponent;
    @ViewChild("movFilterMobile") movFilterMobile!: MovFilterComponent;
    @ViewChild("supplyTypeFilter") supplyTypeFilter!: SupplyTypeFilterComponent;

    facets: FacetWithValues[] = [];
    filteredFacets: FacetWithValues[] = [];
    searchTerm = "";
    manuallyExpanded = false;
    expandedFacetIds = new Set<string>();
    originalFacets: FacetWithValues[] = [];
    private readonly destroy$ = new Subject<void>();
    private readonly searchInput$ = new Subject<string>();

    constructor(
        private route: ActivatedRoute,
        private router: Router
    ) {}

    get filtersExpanded(): boolean {
        return this.manuallyExpanded;
    }

    get hasActiveFilters(): boolean {
        const hasShippingFilters =
            !!this.shippingFilter?.selectedShippingDate ||
            !!this.shippingFilterMobile?.selectedShippingDate;
        const hasMovFilters =
            !!this.movFilter?.selectedMov ||
            !!this.movFilterMobile?.selectedMov;
        const hasSupplyTypeFilter =
            !!this.supplyTypeFilter?.hasActiveFilters();
        return (
            this.activeFacetValueIds.length > 0 ||
            hasShippingFilters ||
            hasMovFilters ||
            hasSupplyTypeFilter
        );
    }

    toggleVisibility() {
        if (window.innerWidth >= 1024) {
            return;
        }

        this.openFilterModal.emit();
        this.manuallyExpanded = !this.manuallyExpanded;
    }

    ngOnChanges(changes: SimpleChanges) {
        if ("facetValues" in changes) {
            const next = changes["facetValues"].currentValue as
                | SearchProductsQuery["search"]["facetValues"]
                | null;
            const hasNext = Array.isArray(next) && next.length > 0;

            if (hasNext) {
                this.facets = this.groupFacetValues(next);
                this.originalFacets = [...this.facets];
                if (this.searchTerm) {
                    this.onSearchTermChange();
                } else {
                    this.filteredFacets = this.facets;
                }
            } else {
                if (this.searchTerm) {
                    this.onSearchTermChange();
                } else {
                    this.filteredFacets = this.originalFacets.length
                        ? this.originalFacets
                        : this.facets;
                }
            }
        }
    }

    onShowMoreClick(facet: FacetWithValues): void {
        const facetId = facet.id;
        const loaded = facet.values.length;
        const hasMoreOnServer = loaded < this.facetTotalItems;

        if (this.expandedFacetIds.has(facetId)) {
            this.expandedFacetIds.delete(facetId);
        } else {
            this.expandedFacetIds.add(facetId);
            if (hasMoreOnServer) {
                this.requestMoreFacets.emit();
            }
        }
    }

    isFacetExpanded(facetId: string): boolean {
        return this.expandedFacetIds.has(facetId);
    }

    isActive(facetValueId: string): boolean {
        return this.activeFacetValueIds.includes(facetValueId);
    }

    ngOnInit(): void {
        this.searchInput$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((term) => {
                this.facetSearchTermChange.emit(term);
            });
        this.route.queryParams
            .pipe(takeUntil(this.destroy$))
            .subscribe((params) => {
                if (params[FACET_SEARCH_ROUTE_QUERY_PARAM] !== undefined) {
                    if (this.facets.length > 0) {
                        this.onSearchTermChange();
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.searchInput$.complete();
    }

    toggleFacetValueIdInRoute(id: string) {
        this.router.navigate(
            buildProductListRouteCommands(
                this.router.url,
                this.toggleFacetValueId(id),
            ),
            {
                queryParams: { page: 1 },
                queryParamsHandling: "merge",
                state: {
                    noScroll: true,
                },
            },
        );
    }

    toggleFacetValueId(id: string): string[] {
        const existing = this.activeFacetValueIds;
        return existing.includes(id)
            ? existing.filter((x) => x !== id)
            : existing.concat(id);
    }

    trackById(_index: number, item: { id: string }) {
        return item.id;
    }

    clearAllFilters(): void {
        this.searchTerm = "";
        if (this.shippingFilter) {
            this.shippingFilter.clearFilters();
        }
        if (this.movFilter) {
            this.movFilter.clearFilters();
        }
        if (this.shippingFilterMobile) {
            this.shippingFilterMobile.clearFilters();
        }
        if (this.movFilterMobile) {
            this.movFilterMobile.clearFilters();
        }
        if (this.supplyTypeFilter) {
            this.supplyTypeFilter.clearFilters();
        }
        this.router.navigate(buildProductListRouteCommands(this.router.url), {
            queryParams: getClearProductListFilterQueryParams(),
            queryParamsHandling: "merge",
            state: {
                noScroll: true,
            },
        });
    }

    onShippingDateFilterChange(selectedRanges: string[]): void {
        this.router.navigate(["./"], {
            queryParams: {
                [SHIPPING_DATE_FILTER_QUERY_PARAM]:
                    selectedRanges.length > 0 ? selectedRanges.join(",") : null,
                page: 1,
            },
            queryParamsHandling: "merge",
            relativeTo: this.route,
            state: {
                noScroll: true,
            },
        });
    }

    onMovFilterChange(selectedRanges: string[]): void {
        this.router.navigate(["./"], {
            queryParams: {
                [MOV_FILTER_QUERY_PARAM]:
                    selectedRanges.length > 0 ? selectedRanges.join(",") : null,
                page: 1,
            },
            queryParamsHandling: "merge",
            relativeTo: this.route,
            state: {
                noScroll: true,
            },
        });
    }

    onSupplyTypeFilterChange(selectedTypes: string[]): void {
        this.router.navigate(["./"], {
            queryParams: {
                [SUPPLY_TYPE_FILTER_QUERY_PARAM]:
                    selectedTypes.length > 0 ? selectedTypes.join(",") : null,
                page: 1,
            },
            queryParamsHandling: "merge",
            relativeTo: this.route,
            state: {
                noScroll: true,
            },
        });
    }

    onSearchTermChange(): void {
        this.filteredFacets = this.originalFacets.map((facet) => ({
            ...facet,
            values: facet.values.filter((value) =>
                value.name.toLowerCase().includes(this.searchTerm.toLowerCase())
            ),
        }));
        this.searchInput$.next(this.searchTerm);
    }

    private groupFacetValues(
        facetValues: SearchProductsQuery["search"]["facetValues"] | null
    ): FacetWithValues[] {
        if (!facetValues) {
            return [];
        }
        const activeFacetValueIds = this.activeFacetValueIds;
        const facetMap = new Map<string, FacetWithValues>();
        for (const {
            count,
            facetValue: { id, name, facet },
        } of facetValues) {
            if (
                count === this.totalResults &&
                !activeFacetValueIds.includes(id)
            ) {
                continue;
            }
            const facetFromMap = facetMap.get(facet.id);
            if (facetFromMap) {
                facetFromMap.values.push({ id, name, count });
            } else {
                facetMap.set(facet.id, {
                    id: facet.id,
                    name: facet.name,
                    values: [{ id, name, count }],
                });
            }
        }
        return Array.from(facetMap.values());
    }
}
